import { Modal, message, Alert, Space, Form, Input } from 'antd';
import PropTypes from 'prop-types';
import React from 'react';
import { t } from 'ttag';

import Label from 'common/components/Label';
import SearchQuerySelect from 'common/components/SearchQuerySelect';

import { organizationSearchQueryOptions } from '../../Organizations/queries';

import { useMoveEventMutation } from './queries';

const MoveEventModal = ({ event, onClose }) => {
  const [form] = Form.useForm();

  const moveEventMutation = useMoveEventMutation();

  const handleCancel = () => {
    onClose(false);
  };

  const handleMove = async values => {
    await moveEventMutation.mutateAsync(
      {
        shortcode: values.event.shortcode,
        organizationId: values.event.organizationId,
      },
      {
        onSuccess: async () => {
          await onClose(true); // wait for onClose to reload table first
          message.success(t`The event has been moved successfully.`);
        },
        onError: error => {
          message.error(error?.message || t`An error occurred while moving the event.`);
        },
      },
    );
  };

  return (
    <Modal
      title={t`Move Event`}
      open
      onCancel={handleCancel}
      okText={t`Move Event`}
      okButtonProps={{
        loading: moveEventMutation.isLoading,
        htmlType: 'submit',
        form: 'move-event-form',
      }}
      cancelText={t`Cancel`}
      cancelButtonProps={{ disabled: moveEventMutation.isLoading }}
      destroyOnClose
    >
      <Form
        form={form}
        id="move-event-form"
        layout="vertical"
        onFinish={handleMove}
        initialValues={{
          event: {
            ...event,
            // keep the organization null as we don't have data for the current organization
            organizationId: null,
          },
        }}
        requiredMark={false}
        disabled={moveEventMutation.isLoading}
      >
        <Space direction="vertical">
          <Alert
            message={t`Moving this event will grant everyone within the selected organization access to this event.`}
            type="warning"
            showIcon
          />

          <Form.Item name={['event', 'shortcode']} hidden>
            <Input disabled />
          </Form.Item>

          <Form.Item label={<Label>{t`Event`}</Label>} name={['event', 'name']}>
            <Input disabled />
          </Form.Item>

          <Form.Item
            label={<Label>{t`Organization`}</Label>}
            name={['event', 'organizationId']}
            rules={[
              { required: true },
              {
                validator: async (_, value) =>
                  value !== event.organizationId ||
                  Promise.reject(t`Please select a different organization.`),
              },
            ]}
          >
            <SearchQuerySelect
              style={{ width: '100%' }}
              placeholder={t`Search organization`}
              getQueryOptions={organizationSearchQueryOptions}
              options={data =>
                data?.map(org => ({
                  label: org.name,
                  value: org.id,
                  disabled: org.id === event.organizationId,
                }))
              }
            />
          </Form.Item>
        </Space>
      </Form>
    </Modal>
  );
};

MoveEventModal.propTypes = {
  event: PropTypes.shape({
    shortcode: PropTypes.string,
    name: PropTypes.string,
    organizationId: PropTypes.string,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default MoveEventModal;
